import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Cpu, Scale, Shield, Brain, Lock, Glasses, Link as LinkIcon, Bot, Satellite, Network, Atom } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { SITE, HOME_PILLARS, STACK, MANDATES, OPERATING_MODELS, FEATURED_INSIGHT, INSIGHTS } from "@/data/content";
import StackOrbit from "@/components/site/StackOrbit";
import LogoWall from "@/components/site/LogoWall";
import CTABand from "@/components/site/CTABand";

const ICONS = { cpu: Cpu, scale: Scale, shield: Shield, brain: Brain, lock: Lock, glasses: Glasses, link: LinkIcon, bot: Bot, satellite: Satellite, network: Network, atom: Atom };

export default function Home() {
  useDocumentTitle("Meta61 — the convergence company");
  const latest = [FEATURED_INSIGHT, ...INSIGHTS].slice(0, 3);

  return (
    <div>
      {/* Hero */}
      <section className="m61-grid-bg border-b border-m61-line overflow-hidden">
        <div className="m61-container py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7 m61-fade-up">
            <p className="m61-eyebrow mb-8">The convergence company · Australia</p>
            <h1 className="m61-h1 mb-8">
              Where technology and <span className="m61-gradient-text">governance converge.</span>
            </h1>
            <p className="m61-lead max-w-xl mb-10">
              Meta61 is an Australian think tank and advisory at the convergence of artificial
              intelligence and emerging technology — engineering systems that are secure,
              sustainable, and sovereign.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/contact" className="m61-btn-primary" data-testid="hero-contact">
                Start a conversation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/focus" className="m61-btn-ghost" data-testid="hero-focus">
                Explore our focus
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 m61-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="max-w-sm mx-auto"><StackOrbit /></div>
          </div>
        </div>
      </section>

      {/* The Meta61 difference */}
      <section className="m61-section">
        <div className="m61-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="m61-eyebrow mb-5">The Meta61 difference — one lens</p>
            <h2 className="m61-h2">Most advisors choose a side. We work across both.</h2>
          </div>
          <div className="lg:col-span-7 text-m61-muted leading-relaxed text-lg">
            The legal and policy firms govern what they cannot build. The integrators build what
            they do not govern. Meta61 works across both from a single vantage point — joining deep
            technical capability with standards-grade governance — so that the technology you adopt
            is trusted by design.
          </div>
        </div>
        <div className="m61-container grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          {HOME_PILLARS.map((p) => {
            const Icon = ICONS[p.icon] || Cpu;
            return (
              <div key={p.title} className="m61-card p-8">
                <div className="w-12 h-12 rounded-full flex items-center justify-center border border-m61-line-strong mb-6">
                  <Icon className="w-5 h-5 text-m61-cyan" />
                </div>
                <h3 className="m61-h3 mb-2 m61-gradient-text">{p.title}</h3>
                <p className="text-m61-muted">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* The convergence stack */}
      <section className="m61-section border-t border-m61-line bg-m61-surface/40">
        <div className="m61-container">
          <div className="max-w-2xl mb-12">
            <p className="m61-eyebrow mb-5">The convergence stack</p>
            <h2 className="m61-h2 mb-4">AI is the cornerstone.</h2>
            <p className="m61-lead">
              Around it, a converging set of technologies is reshaping enterprises, nations, and
              the public good.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-m61-line border border-m61-line">
            {STACK.map((s) => {
              const Icon = ICONS[s.icon] || Cpu;
              return (
                <div key={s.name} className="bg-m61-surface p-6 hover:bg-m61-surface2 transition-colors">
                  <Icon className="w-5 h-5 text-m61-cyan mb-4" />
                  <h3 className="text-m61-text font-display text-base mb-1">{s.name}</h3>
                  {s.tag && <span className="font-mono text-[0.6rem] uppercase tracking-wider text-m61-violet">{s.tag}</span>}
                </div>
              );
            })}
          </div>
          <Link to="/focus" className="m61-btn-ghost mt-8">
            See the stack in depth <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Two mandates */}
      <section className="m61-section">
        <div className="m61-container">
          <p className="m61-eyebrow mb-10">Two mandates</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {MANDATES.map((m) => (
              <Link key={m.title} to="/frameworks" className="m61-card p-8 lg:p-10 group">
                <h3 className="m61-h3 mb-3">{m.title}</h3>
                <p className="text-m61-muted leading-relaxed mb-6">{m.body}</p>
                <span className="text-m61-cyan text-sm inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                  Explore frameworks <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Operating models */}
      <section className="m61-section border-t border-m61-line">
        <div className="m61-container">
          <p className="m61-eyebrow mb-10">Our operating models</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {OPERATING_MODELS.map((o) => (
              <Link key={o.name} to={o.to} className="m61-card p-8 lg:p-10 group">
                <h3 className="m61-h3 mb-3 m61-gradient-text">{o.name}</h3>
                <p className="text-m61-muted leading-relaxed">{o.body}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Logo wall */}
      <LogoWall />

      {/* Featured experience */}
      <section className="m61-section">
        <div className="m61-container">
          <div className="relative overflow-hidden rounded-2xl border border-m61-line bg-m61-surface p-10 lg:p-16">
            <div className="m61-grid-bg absolute inset-0 opacity-30" aria-hidden="true" />
            <div className="relative max-w-2xl">
              <p className="m61-eyebrow mb-5">Featured experience</p>
              <h2 className="m61-h2 mb-4">How ready are you for the <span className="m61-gradient-text">convergence?</span></h2>
              <p className="m61-lead mb-8">
                A short, confidential self-assessment across strategy, governance, risk, trust, and
                sustainability — mapped to the standards that matter. Get your maturity profile in minutes.
              </p>
              <Link to="/resources#assessment" className="m61-btn-primary" data-testid="home-assessment">
                Take the assessment <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Latest insights */}
      <section className="m61-section border-t border-m61-line">
        <div className="m61-container">
          <div className="flex items-end justify-between mb-12 gap-6">
            <div>
              <p className="m61-eyebrow mb-5">Latest insights</p>
              <h2 className="m61-h2">Thinking at the convergence.</h2>
            </div>
            <Link to="/insights" className="m61-btn-ghost hidden sm:inline-flex">
              All insights <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {latest.map((a) => (
              <a key={a.title} href={a.url} target="_blank" rel="noopener noreferrer" className="m61-card p-8 flex flex-col">
                <span className="font-mono text-[0.65rem] uppercase tracking-wider text-m61-violet mb-4">{a.theme}</span>
                <h3 className="m61-h3 mb-3">{a.title}</h3>
                <p className="text-m61-muted text-sm flex-1 line-clamp-4">{a.summary}</p>
                <span className="text-m61-cyan text-sm inline-flex items-center gap-2 mt-6">Read more <ArrowUpRight className="w-4 h-4" /></span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <CTABand showAssessment />
    </div>
  );
}
